// Shared building blocks used by every Dashboard tab.
// Keeping these in one file avoids deep import chains across 16 tab files.
import { useState } from "react";
import { Check, ChevronDown, Plus, Lock, Zap } from "lucide-react";

export const PILL =
  "px-4 py-2.5 rounded-xl font-semibold border-2 border-black text-sm flex items-center gap-2 nb-press";

export function Input({ testId, ...props }) {
  return (
    <input
      data-testid={testId}
      {...props}
      className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm bg-white"
    />
  );
}

export function TextareaField({ testId, ...props }) {
  return (
    <textarea
      data-testid={testId}
      rows={3}
      {...props}
      className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm bg-white"
    />
  );
}

export function FieldLabel({ children }) {
  return (
    <div className="text-xs uppercase font-bold text-gray-600 mt-3 mb-1 first:mt-0">
      {children}
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase font-bold text-gray-600 mb-1">{label}</div>
      {children}
    </div>
  );
}

export function EditorSection({ title, children, testId }) {
  return (
    <div className="nb-card p-4" data-testid={testId}>
      <div className="font-display font-extrabold mb-3">{title}</div>
      {children}
    </div>
  );
}

export function InfoBlock({ title, children }) {
  return (
    <div className="nb-card p-4">
      <div className="text-xs uppercase font-bold text-gray-600 mb-2">{title}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

export function StatCard({ label, value, testId }) {
  return (
    <div className="nb-card-flat p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest font-extrabold text-gray-500">{label}</div>
      <div className="text-2xl font-extrabold mt-1">{value}</div>
    </div>
  );
}

export function LockedFeature({ title, plan, onUpgrade }) {
  return (
    <div className="nb-card p-8 text-center" data-testid="locked-feature">
      <div className="w-16 h-16 rounded-2xl bg-[#FFE6EC] border-2 border-black mx-auto flex items-center justify-center mb-4">
        <Lock className="w-7 h-7" />
      </div>
      <div className="font-display text-2xl font-extrabold">{title} is a paid feature</div>
      <p className="text-gray-700 mt-2 max-w-md mx-auto">
        Upgrade to <b>Launch</b>, <b>Growth</b>, or <b>Scale</b> to unlock {title.toLowerCase()} and connect a custom domain.
      </p>
      <button onClick={onUpgrade} className={`${PILL} bg-[#8B5CF6] text-white mt-6 mx-auto`} data-testid="locked-upgrade-btn">
        <Zap className="w-4 h-4" /> See plans
      </button>
    </div>
  );
}

export function PlanPill({ plan, onClick }) {
  const id = plan?.plan_id || "free";
  const map = {
    free: { label: "FREE", bg: "bg-gray-200", txt: "text-black" },
    launch: { label: "LAUNCH", bg: "bg-[#8B5CF6]", txt: "text-white" },
    growth: { label: "GROWTH", bg: "bg-[#10B981]", txt: "text-white" },
    scale: { label: "SCALE", bg: "bg-[#FF7F50]", txt: "text-white" },
  };
  const s = map[id] || map.free;
  return (
    <button
      onClick={onClick}
      data-testid="plan-pill"
      className={`px-3 py-1.5 rounded-full text-xs font-extrabold border-2 border-black ${s.bg} ${s.txt} nb-press`}
      style={{ boxShadow: "3px 3px 0 0 #111" }}
    >
      {s.label}
    </button>
  );
}

export function StatusPill({ status }) {
  const map = {
    connected: { bg: "#10B981", t: "Connected" },
    pending: { bg: "#F59E0B", t: "Pending DNS" },
    failed: { bg: "#EF4444", t: "Failed" },
  };
  const s = map[status] || { bg: "#9CA3AF", t: status || "Not connected" };
  return (
    <span
      className="px-2 py-1 rounded-full text-xs font-bold border-2 border-black"
      style={{ background: s.bg, color: "white" }}
    >
      {s.t}
    </span>
  );
}

export function BusinessSwitcher({ businesses, activeId, onSwitch, onCreate, canCreate }) {
  const [open, setOpen] = useState(false);
  if (businesses.length <= 1 && !canCreate) return null;
  return (
    <div className="relative ml-2" data-testid="biz-switcher">
      <button
        onClick={() => setOpen((v) => !v)}
        className="px-3 py-2 rounded-xl border-2 border-black font-bold text-xs bg-[#FFEEDD] flex items-center gap-1.5 nb-press"
        data-testid="biz-switcher-toggle"
      >
        <span className="hidden sm:inline">Switch</span>
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            className="absolute left-0 mt-2 w-72 bg-white border-2 border-black rounded-xl nb-shadow-lg z-40 p-2"
            data-testid="biz-switcher-menu"
          >
            <div className="text-[10px] uppercase tracking-widest font-extrabold text-gray-500 px-2 py-1.5">
              Your businesses
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {businesses.map((b) => (
                <button
                  key={b.business_id}
                  onClick={() => { onSwitch(b.business_id); setOpen(false); }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-semibold flex items-center justify-between gap-2 ${
                    b.business_id === activeId ? "bg-[#FFEEDD]" : "hover:bg-gray-50"
                  }`}
                  data-testid={`biz-option-${b.business_id}`}
                >
                  <span className="truncate">{b.summary?.name || b.slug}</span>
                  {b.business_id === activeId && <Check className="w-4 h-4 shrink-0" />}
                </button>
              ))}
            </div>
            <button
              onClick={() => { onCreate(); setOpen(false); }}
              className={`mt-2 w-full px-3 py-2 rounded-lg text-sm font-bold border-2 border-black flex items-center gap-2 ${
                canCreate ? "bg-[#8B5CF6] text-white" : "bg-white text-gray-500"
              }`}
              data-testid="biz-add-btn"
            >
              <Plus className="w-4 h-4" />
              {canCreate ? "Add another business" : "Upgrade for multiple businesses"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
